import { RenderPass } from "@/core/pipeline/render-pass"
import { Texture } from "@/core/backends/webgl/texture"
import { Renderer } from "@/core/pipeline/renderer"
import { Photo } from "@/core/photo"

export class PreprocessPass extends RenderPass {
	readBuffer: Texture
	writeBuffer: Texture

	reprocessInfo: {
		id: string
		correctsDistortion: boolean
	} | null = null

	constructor(renderer: Renderer, id: string) {
		super(renderer, id)

		this.readBuffer = renderer.backend.createTexture({
			label: "preprocess.readBuffer",
			format: renderer.backend.textureFormat,
		})
		this.writeBuffer = renderer.backend.createTexture({
			label: "preprocess.writeBuffer",
			format: renderer.backend.textureFormat,
		})
	}

	public render(input: Texture) {
		const { renderer } = this
		const { photo } = renderer

		this.renderer.performanceCounters.begin("renderDehaze")

		// Do not recompute if photos are the same and lens correction is the same
		if (this.checkShouldReprocess(photo) === false) {
			// TODO: fix for handling crop + transform
			// return
		}

		// Compute target size
		const { width, height } = renderer.photo.texture
		const scale = 256 / Math.max(width, height) // scale to fit
		const targetWidth = Math.floor(width * scale)
		const targetHeight = Math.floor(height * scale)

		// Setup buffers

		const meanA = renderer.filteredTextures.meanA
		const meanB = renderer.filteredTextures.meanB

		meanA.updateSize(targetWidth, targetHeight)
		meanB.updateSize(targetWidth, targetHeight)

		this.writeBuffer.updateSize(targetWidth, targetHeight)
		this.readBuffer.updateSize(targetWidth, targetHeight)

		// Downsample input texture to target size
		const downsampledTexture = this.guidedDownsample(input, this.readBuffer)

		// Reuse textures

		const II = meanA
		const I = meanB
		const p = meanA
		const mean_II = meanB
		const mean_I = downsampledTexture

		// For dehaze
		this.guidedDarkChannel(downsampledTexture, p)

		// Is, Im, p, I
		this.guidedPreprocess(downsampledTexture, p, I)

		// IIs, IIm, Ip, II
		this.guidedMul(I, II)

		this.guidedMean(I, mean_I)
		this.guidedMean(II, mean_II)

		// computeA(editor, mean_I, mean_II, 0.005, meanA)
		this.computeA(mean_I, mean_II, meanA)
		this.computeB(mean_I, meanA, meanB)

		this.guidedMean(meanA, meanA)
		this.guidedMean(meanB, meanB)

		this.renderer.performanceCounters.end("renderDehaze")
	}

	public dispose() {
		this.readBuffer.reset()
		this.writeBuffer.reset()
	}

	private checkShouldReprocess(photo: Photo) {
		const currentInfo = this.reprocessInfo
		this.reprocessInfo = {
			id: photo.id,
			correctsDistortion: photo.adjustments.correctLensDistortion,
		}

		if (
			currentInfo?.id !== this.reprocessInfo.id ||
			currentInfo?.correctsDistortion !== this.reprocessInfo.correctsDistortion
		) {
			return true
		}

		return false
	}

	private guidedDownsample(texture: Texture, out: Texture) {
		out.bindFramebuffer()

		texture.bind(0)

		this.renderer.shaders.guidedDownsample.render({
			inputTexture: 0,
			offset: [(1 / out.width) * 0.5, (1 / out.height) * 0.5],
		})

		return out
	}

	private computeA(mean_I: Texture, mean_II: Texture, out: Texture) {
		out.bindFramebuffer()

		mean_I.bind(0)
		mean_II.bind(1)

		this.renderer.shaders.guidedComputeA.render({
			mean_I: 0,
			mean_II: 1,
		})

		return out
	}

	private computeB(mean_I: Texture, a: Texture, out: Texture) {
		out.bindFramebuffer()

		mean_I.bind(0)
		a.bind(1)

		this.renderer.shaders.guidedComputeB.render({
			mean_I: 0,
			a: 1,
		})

		return out
	}

	private guidedMul(inputTexture: Texture, out: Texture) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		this.renderer.shaders.guidedMul.render({
			inputTexture: 0,
		})

		return out
	}

	private guidedPreprocess(
		inputTexture: Texture,
		inputDarkChannel: Texture,
		outputTexture: Texture,
	) {
		outputTexture.bindFramebuffer()

		inputTexture.bind(0)
		inputDarkChannel.bind(1)

		this.renderer.shaders.guidedPreprocess.render({
			inputTexture: 0,
			inputDarkChannel: 1,
		})

		return outputTexture
	}

	private guidedDarkChannel(inputTexture: Texture, outputTexture: Texture) {
		const renderPass = (input: Texture, output: Texture, direction: [number, number]) => {
			output.bindFramebuffer()

			input.bind(0)

			this.renderer.shaders.guidedDarkChannel.render({
				inputTexture: 0,
				direction: direction,
			})
		}

		renderPass(inputTexture, this.writeBuffer, [1, 0])
		renderPass(this.writeBuffer, outputTexture, [0, 1])

		return outputTexture
	}

	private guidedMean(inputTexture: Texture, outputTexture: Texture) {
		const renderPass = (input: Texture, output: Texture, direction: [number, number]) => {
			output.bindFramebuffer()

			input.bind(0)

			this.renderer.shaders.guidedMean.render({
				inputTexture: 0,
				direction: [direction[0] / input.width, direction[1] / input.height],
			})
		}

		renderPass(inputTexture, this.writeBuffer, [1, 0])
		renderPass(this.writeBuffer, outputTexture, [0, 1])

		return outputTexture
	}
}

/*function recursiveDownsample(
	editor: Editor,
	texture: Texture,
	targetWidth: number,
	targetHeight: number,
) {
	let width = Math.floor(texture.width / 2)
	let height = Math.floor(texture.height / 2)

	// Recursively downsample the texture in half until the target size is reached
	if (targetWidth < width || targetHeight < height) {
		while (width > targetWidth && height > targetHeight) {
			texture = guidedDownsample(editor, texture, width, height)

			width = Math.floor(width / 2)
			height = Math.floor(height / 2)
		}
	}

	return guidedDownsample(editor, texture, targetWidth, targetHeight)
}*/

/*function downsample(editor: Editor, texture: Texture, width: number, height: number) {
	editor.pipeline.writeBuffer.updateSize(width, height)
	editor.pipeline.writeBuffer.bindFramebuffer()

	texture.bind(0)

	editor.shaders.basic.use()
	editor.shaders.basic.setUniforms({
		inputTexture: 0,
	})
	editor.shaders.basic.draw(editor.buffer)

	editor.swapBuffers()

	return editor.readBuffer
}*/

/*function displayTexture(editor: Editor, texture: Texture) {
	const viewMatrix = editor.getViewMatrix()

	texture.bind(0)

	
	editor.clear()

	editor.shaders.basic.use()
	editor.shaders.basic.setUniforms({
		viewMatrix,
		inputTexture: 0,
	})
	editor.shaders.basic.draw(editor.buffer)
}*/
